import React, { useEffect, useState } from "react";
import axios from "axios";
import { ChefHat, Palette, Dumbbell, Bus, Music, Leaf } from "lucide-react";
import { PageHeader, Section, Card, PlaceholderImage, ButtonLink } from "../components/ui-kit";
import { TRIPS, ACTIVITY_PHOTOS } from "../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const resolveSrc = (src) => (src?.startsWith("http") ? src : `${BACKEND}${src}`);

const ACTIVITIES = [
  { icon: ChefHat, title: "Cooking and food", text: "Preparing meals and snacks, building kitchen confidence and healthy routines." },
  { icon: Palette, title: "Arts and creativity", text: "Painting, crafts and creative projects to express ideas and have fun." },
  { icon: Dumbbell, title: "Movement and wellbeing", text: "Gentle exercise, sport and activities that support physical and mental health." },
  { icon: Bus, title: "Community trips", text: "Getting out and about, using local places and travelling with growing confidence." },
  { icon: Music, title: "Music and social time", text: "Music, games and group sessions that build friendships and connection." },
  { icon: Leaf, title: "Outdoors and nature", text: "Gardening, walks and time outside to relax and enjoy the natural world." },
];

export default function WhatWeDo() {
  const [gallery, setGallery] = useState({ activities: ACTIVITY_PHOTOS, trips: TRIPS });

  useEffect(() => {
    axios
      .get(`${API}/gallery`)
      .then((r) =>
        setGallery({
          activities: r.data.activities?.length ? r.data.activities : ACTIVITY_PHOTOS,
          trips: r.data.trips?.length ? r.data.trips : TRIPS,
        })
      )
      .catch(() => {});
  }, []);

  return (
    <>
      <PageHeader
        eyebrow="What we do"
        title="Active, meaningful days"
        intro="Our days blend life skills, creativity, movement, community and connection. Every activity is chosen with the people we support, never imposed on them."
        testid="what-we-do-header"
      />
      <Section tone="default">
        <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
          {ACTIVITIES.map((a) => (
            <Card key={a.title} data-testid={`activity-card-${a.title.split(" ")[0].toLowerCase()}`}>
              <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-orange-50 text-orange-700">
                <a.icon className="h-6 w-6" aria-hidden="true" />
              </span>
              <h2 className="mt-5 font-heading text-xl font-semibold text-stone-900">{a.title}</h2>
              <p className="mt-2 text-lg text-stone-700 leading-relaxed">{a.text}</p>
            </Card>
          ))}
        </div>
      </Section>

      <Section tone="white">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <PlaceholderImage
            src="https://customer-assets.emergentagent.com/job_spire-support-hub/artifacts/uecqaya5_20260623_135433.jpg"
            alt="Someone we support concentrating while shaping clay with a rolling pin in a craft session."
          />
          <div>
            <h2 className="font-heading text-3xl font-semibold text-stone-900">Chosen together</h2>
            <p className="mt-4 text-lg text-stone-700 leading-relaxed">
              We plan activities with each person, based on what they enjoy and what they want to
              work towards. People can try new things, return to favourites, or take quiet time when
              they need it. There is no pressure and no fixed timetable that everyone must follow.
            </p>
            <ButtonLink to="/a-typical-week" variant="secondary" className="mt-8" data-testid="what-we-do-week-button">
              See a typical week
            </ButtonLink>
          </div>
        </div>
      </Section>

      <Section tone="white" data-testid="activities-gallery">
        <div className="max-w-3xl">
          <h2 className="font-heading text-3xl font-semibold text-stone-900">
            Sports and meaningful activities
          </h2>
          <p className="mt-4 text-lg text-stone-700 leading-relaxed">
            From football and hockey to dance, clay modelling and summer water fights, our days are
            full of active, meaningful things to do. Here are some of the people we support enjoying
            them.
          </p>
        </div>
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {gallery.activities.map((p, i) => (
            <figure
              key={p.id || i}
              className="overflow-hidden rounded-3xl bg-stone-200 border border-stone-200 aspect-square"
              data-testid={`activity-photo-${i}`}
            >
              <img
                src={resolveSrc(p.src)}
                alt={p.alt}
                loading="lazy"
                className="h-full w-full object-cover transition-transform duration-300 hover:scale-105"
              />
            </figure>
          ))}
        </div>
      </Section>

      <Section tone="tinted" data-testid="trips-gallery">
        <div className="max-w-3xl">
          <h2 className="font-heading text-3xl font-semibold text-stone-900">Our trips out</h2>
          <p className="mt-4 text-lg text-stone-700 leading-relaxed">
            Getting out and about is one of the best parts of the week. Here are some happy moments
            from days out the people we support have enjoyed together.
          </p>
        </div>
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {gallery.trips.map((t, i) => (
            <figure
              key={t.id || i}
              className="overflow-hidden rounded-3xl bg-stone-200 border border-stone-200 aspect-square"
              data-testid={`trip-photo-${i}`}
            >
              <img
                src={resolveSrc(t.src)}
                alt={t.alt}
                loading="lazy"
                className="h-full w-full object-cover transition-transform duration-300 hover:scale-105"
              />
            </figure>
          ))}
        </div>
      </Section>
    </>
  );
}
